import type { z } from "zod";
import { ServiceError } from "./serviceError";
import type { ValidationError } from "./validationError";

/**
 * Serializable success/failure envelope for app boundaries
 * (server actions, tRPC, clients) where Error subclasses lose fields.
 */
export type ServiceResult<T> =
  | { ok: true; data: T }
  | {
      ok: false;
      code: string;
      statusCode?: number;
      validationErrors?: ValidationError[];
      issues?: z.core.$ZodIssue[];
    };

export function serviceErrorToResult(error: unknown): ServiceResult<never> {
  if (error instanceof ServiceError) {
    const details = error.extensions?.details;
    return {
      ok: false,
      code: error.extensions?.code ?? "SERVICE_ERROR",
      statusCode: error.extensions?.statusCode,
      validationErrors: details?.validationErrors,
      issues: details?.issues,
    };
  }

  return { ok: false, code: "SERVICE_ERROR" };
}

export async function toServiceResult<T>(
  fn: () => Promise<T>,
): Promise<ServiceResult<T>> {
  try {
    return { ok: true, data: await fn() };
  } catch (error) {
    return serviceErrorToResult(error);
  }
}

export type MapValidationError = (
  error: ValidationError,
) => [string, string] | undefined;

export type ServiceResultFormError =
  | { fields: Record<string, string> }
  | string;

/**
 * Map a failed ServiceResult into form-level feedback.
 * Domain-specific validation → field mapping is injected by the caller.
 */
export function serviceResultToFormError(
  result: ServiceResult<unknown>,
  options?: {
    mapValidationError?: MapValidationError;
    fieldPrefix?: string;
    fallback?: string;
  },
): ServiceResultFormError | undefined {
  if (result.ok) return undefined;

  const fallback = options?.fallback ?? "internalError";
  const prefix = options?.fieldPrefix;

  if (result.validationErrors?.length && options?.mapValidationError) {
    const fields = Object.fromEntries(
      result.validationErrors
        .map(options.mapValidationError)
        .filter((entry): entry is [string, string] => entry !== undefined)
        .map(([path, message]) => [
          prefix ? `${prefix}.${path}` : path,
          message,
        ]),
    );
    if (Object.keys(fields).length > 0) {
      return { fields };
    }
  }

  if (result.issues?.length) {
    const fields = Object.fromEntries(
      result.issues.map((issue) => [
        [...(prefix ? [prefix] : []), ...issue.path].join("."),
        typeof issue.message === "string" ? issue.message : fallback,
      ]),
    );
    if (Object.keys(fields).length > 0) {
      return { fields };
    }
  }

  return fallback;
}
